import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Shield, Lock, FileText, CheckCircle, AlertTriangle, Users, Database, Server, Award, RefreshCw, TrendingUp, Search, Download, ExternalLink, Activity } from 'lucide-react';
import PageLayout from '../../components/ui/PageLayout';
import Icon from '../../components/AppIcon';

const Security = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [securityStatus, setSecurityStatus] = useState({
    systemStatus: 'operational',
    lastScan: '2025-01-10T10:00:00Z',
    threatsBlocked: 1247,
    uptime: 99.98
  });

  const securityTabs = [
    { id: 'overview', label: 'Security Overview', icon: Shield, color: 'blue' },
    { id: 'measures', label: 'Security Measures', icon: Lock, color: 'purple' },
    { id: 'compliance', label: 'Compliance', icon: FileText, color: 'green' },
    { id: 'monitoring', label: 'Monitoring', icon: Activity, color: 'orange' },
    { id: 'certifications', label: 'Certifications', icon: Award, color: 'red' },
    { id: 'user-controls', label: 'User Controls', icon: Users, color: 'indigo' }
  ];

  const securityData = {
    overview: {
      title: 'Comprehensive Security Framework',
      content: [
        {
          icon: Database,
          title: 'Data Protection',
          description: 'End-to-end encryption, secure data storage, and privacy-first architecture.',
          features: [
            'AES-256 encryption for data at rest',
            'TLS 1.3 for data in transit',
            'Zero-trust security model',
            'Regular data backup and recovery'
          ]
        },
        {
          icon: Server,
          title: 'Infrastructure Security',
          description: 'Robust infrastructure with multiple layers of protection and monitoring.',
          features: [
            'AWS SOC 2 compliant infrastructure',
            'Multi-zone redundancy',
            'DDoS protection and mitigation',
            '24/7 security monitoring'
          ]
        },
        {
          icon: Users,
          title: 'Access Control',
          description: 'Strict authentication and authorization protocols for all user levels.',
          features: [
            'Multi-factor authentication (MFA)',
            'Role-based access control (RBAC)',
            'Session management and timeout',
            'API rate limiting and throttling'
          ]
        }
      ]
    },
    measures: {
      title: 'Technical Security Measures',
      content: [
        {
          category: 'Network Security',
          measures: [
            'Firewall protection with intrusion detection',
            'VPN access for administrative functions',
            'Network segmentation and isolation',
            'Regular penetration testing'
          ]
        },
        {
          category: 'Application Security',
          measures: [
            'Secure coding practices and code reviews',
            'OWASP Top 10 vulnerability protection',
            'Input validation and sanitization',
            'Cross-site scripting (XSS) prevention'
          ]
        },
        {
          category: 'Data Security',
          measures: [
            'Database encryption and access logging',
            'Personal data anonymization techniques',
            'Secure data deletion procedures',
            'Data loss prevention (DLP) systems'
          ]
        }
      ]
    },
    compliance: {
      title: 'Regulatory Compliance',
      frameworks: [
        {
          name: 'GDPR',
          description: 'General Data Protection Regulation compliance for EU users',
          status: 'Compliant',
          details: [
            'Data protection impact assessments',
            'Privacy by design implementation',
            'User consent management',
            'Right to be forgotten procedures'
          ]
        },
        {
          name: 'CCPA',
          description: 'California Consumer Privacy Act compliance',
          status: 'Compliant',
          details: [
            'Consumer rights disclosure',
            'Data selling prohibition',
            'Privacy policy transparency',
            'Data deletion upon request'
          ]
        },
        {
          name: 'SOC 2 Type II',
          description: 'Service Organization Control 2 certification',
          status: 'Certified',
          details: [
            'Security control effectiveness',
            'Availability and processing integrity',
            'Confidentiality measures',
            'Annual third-party audits'
          ]
        }
      ]
    },
    monitoring: {
      title: 'Security Monitoring & Incident Response',
      systems: [
        {
          name: 'Real-time Threat Detection',
          description: 'Advanced threat detection using AI and machine learning',
          capabilities: [
            'Anomaly detection algorithms',
            'Behavioral analysis patterns',
            'Automated threat response',
            'Security incident classification'
          ]
        },
        {
          name: 'Security Information and Event Management (SIEM)',
          description: 'Comprehensive logging and monitoring system',
          capabilities: [
            'Centralized log management',
            'Real-time alert generation',
            'Forensic analysis capabilities',
            'Compliance reporting automation'
          ]
        },
        {
          name: 'Incident Response Plan',
          description: 'Structured approach to security incident handling',
          capabilities: [
            '24/7 security operations center',
            'Incident escalation procedures',
            'Communication protocols',
            'Recovery and lessons learned'
          ]
        }
      ]
    },
    certifications: {
      title: 'Security Certifications & Audits',
      certifications: [
        {
          name: 'ISO 27001',
          description: 'Information Security Management System',
          status: 'In Progress',
          validUntil: '2025-12-31',
          scope: 'Information security management for SaaS platform'
        },
        {
          name: 'SOC 2 Type II',
          description: 'Security, Availability, and Confidentiality',
          status: 'Current',
          validUntil: '2025-12-31',
          scope: 'Security controls for customer data processing'
        },
        {
          name: 'PCI DSS',
          description: 'Payment Card Industry Data Security Standard',
          status: 'Compliant',
          validUntil: '2025-12-31',
          scope: 'Payment processing and cardholder data protection'
        }
      ]
    },
    userControls: {
      title: 'User Security Controls',
      controls: [
        {
          category: 'Account Security',
          options: [
            'Two-factor authentication setup',
            'Password strength requirements',
            'Session timeout configuration',
            'Login history and activity monitoring'
          ]
        },
        {
          category: 'Data Privacy',
          options: [
            'Data export and portability',
            'Privacy settings management',
            'Communication preferences',
            'Data deletion requests'
          ]
        },
        {
          category: 'Access Management',
          options: [
            'Team permissions and roles',
            'API key management',
            'Integration security settings',
            'Audit log access'
          ]
        }
      ]
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational': case'Compliant': case'Current':
        return 'text-green-600 bg-green-100';
      case 'In Progress':
        return 'text-yellow-600 bg-yellow-100';
      case 'Certified':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatLastScan = (timestamp) => {
    return new Date(timestamp)?.toLocaleString();
  };

  const filteredContent = () => {
    if (!searchQuery) return securityData?.[activeTab];
    
    const data = securityData?.[activeTab];
    if (data?.content) {
      return {
        ...data,
        content: data?.content?.filter(item =>
          item?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
          item?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
        )
      };
    }
    return data;
  };

  const getTabColorClasses = (color, isActive) => {
    const colorSchemes = {
      blue: {
        active: 'bg-blue-100 text-blue-700 border-blue-200 shadow-blue-200/50',
        inactive: 'text-gray-600 hover:text-blue-700 hover:bg-blue-50 border-transparent hover:border-blue-200',
        icon: isActive ? 'text-blue-600' : 'text-gray-500 group-hover:text-blue-600'
      },
      purple: {
        active: 'bg-purple-100 text-purple-700 border-purple-200 shadow-purple-200/50',
        inactive: 'text-gray-600 hover:text-purple-700 hover:bg-purple-50 border-transparent hover:border-purple-200',
        icon: isActive ? 'text-purple-600' : 'text-gray-500 group-hover:text-purple-600'
      },
      green: {
        active: 'bg-green-100 text-green-700 border-green-200 shadow-green-200/50',
        inactive: 'text-gray-600 hover:text-green-700 hover:bg-green-50 border-transparent hover:border-green-200',
        icon: isActive ? 'text-green-600' : 'text-gray-500 group-hover:text-green-600'
      },
      orange: {
        active: 'bg-orange-100 text-orange-700 border-orange-200 shadow-orange-200/50',
        inactive: 'text-gray-600 hover:text-orange-700 hover:bg-orange-50 border-transparent hover:border-orange-200',
        icon: isActive ? 'text-orange-600' : 'text-gray-500 group-hover:text-orange-600'
      },
      red: {
        active: 'bg-red-100 text-red-700 border-red-200 shadow-red-200/50',
        inactive: 'text-gray-600 hover:text-red-700 hover:bg-red-50 border-transparent hover:border-red-200',
        icon: isActive ? 'text-red-600' : 'text-gray-500 group-hover:text-red-600'
      },
      indigo: {
        active: 'bg-indigo-100 text-indigo-700 border-indigo-200 shadow-indigo-200/50',
        inactive: 'text-gray-600 hover:text-indigo-700 hover:bg-indigo-50 border-transparent hover:border-indigo-200',
        icon: isActive ? 'text-indigo-600' : 'text-gray-500 group-hover:text-indigo-600'
      }
    };

    return colorSchemes?.[color] || colorSchemes?.blue;
  };

  return (
    <>
      <Helmet>
        <title>Security | LeadGen Pro</title>
        <meta name="description" content="Comprehensive security measures, compliance certifications, and data protection protocols for LeadGen Pro platform." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <PageLayout>
        {/* Hero Section */}
        <div className="bg-white border-b rounded-lg shadow-sm">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-center mb-6"
              >
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <Shield className="w-8 h-8 text-red-600" />
                </div>
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-4xl font-bold text-gray-900 mb-4"
              >
                Security & Compliance
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-xl text-gray-600 mb-8"
              >
                Enterprise-grade security measures protecting your data and privacy
              </motion.p>

              {/* Security Status Dashboard */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto"
              >
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-900">System Status</span>
                  </div>
                  <div className={`px-2 py-1 rounded text-sm font-medium ${getStatusColor(securityStatus?.systemStatus)}`}>
                    Operational
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <RefreshCw className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-blue-900">Last Security Scan</span>
                  </div>
                  <div className="text-sm text-blue-700">
                    {formatLastScan(securityStatus?.lastScan)}
                  </div>
                </div>

                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <span className="font-medium text-red-900">Threats Blocked</span>
                  </div>
                  <div className="text-lg font-semibold text-red-700">
                    {securityStatus?.threatsBlocked?.toLocaleString()}
                  </div>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                    <span className="font-medium text-purple-900">Uptime</span>
                  </div>
                  <div className="text-lg font-semibold text-purple-700">
                    {securityStatus?.uptime}%
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Security Navigation */}
        <div className="bg-white border-b rounded-lg shadow-sm">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-2 py-6">
              {securityTabs?.map((tab) => {
                const Icon = tab?.icon;
                const isActive = activeTab === tab?.id;
                const colorClasses = getTabColorClasses(tab?.color, isActive);
                
                return (
                  <motion.button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`group flex items-center gap-3 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300 border-2 shadow-lg hover:shadow-xl transform hover:-translate-y-1 ${
                      isActive ? colorClasses?.active : colorClasses?.inactive
                    }`}
                  >
                    <Icon className={`w-5 h-5 transition-all duration-300 ${colorClasses?.icon}`} />
                    <span className="font-medium">{tab?.label}</span>
                    {isActive && (
                      <motion.div
                        layoutId="activeTab"
                        className="w-2 h-2 rounded-full bg-current opacity-60"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Search and Actions */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between mb-8">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search security information..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value || '')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
              <Download className="w-4 h-4" />
              Security Report
            </button>
            <button className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
              <ExternalLink className="w-4 h-4" />
              Trust Center
            </button>
          </div>
        </div>

        {/* Dynamic Content Rendering */}
        <div className="bg-white rounded-lg shadow-sm border">
          {activeTab === 'overview' && (
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {filteredContent()?.title}
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {filteredContent()?.content?.map((item, index) => {
                  const Icon = item?.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="border rounded-lg p-6"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Icon className="w-5 h-5 text-blue-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {item?.title}
                        </h3>
                      </div>
                      <p className="text-gray-600 mb-4">{item?.description}</p>
                      <ul className="space-y-2">
                        {item?.features?.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'measures' && (
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {securityData?.measures?.title}
              </h2>
              <div className="space-y-6">
                {securityData?.measures?.content?.map((category, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border rounded-lg p-6"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {category?.category}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {category?.measures?.map((measure, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="text-gray-700">{measure}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'compliance' && (
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {securityData?.compliance?.title}
              </h2>
              <div className="space-y-6">
                {securityData?.compliance?.frameworks?.map((framework, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border rounded-lg p-6"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {framework?.name}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(framework?.status)}`}>
                        {framework?.status}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-4">{framework?.description}</p>
                    <ul className="space-y-2">
                      {framework?.details?.map((detail, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'monitoring' && (
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {securityData?.monitoring?.title}
              </h2>
              <div className="space-y-6">
                {securityData?.monitoring?.systems?.map((system, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border rounded-lg p-6"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {system?.name}
                    </h3>
                    <p className="text-gray-600 mb-4">{system?.description}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {system?.capabilities?.map((capability, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="text-gray-700">{capability}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'certifications' && (
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {securityData?.certifications?.title}
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {securityData?.certifications?.certifications?.map((cert, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border rounded-lg p-6"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {cert?.name}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(cert?.status)}`}>
                        {cert?.status}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-3">{cert?.description}</p>
                    <div className="space-y-2 text-sm text-gray-700">
                      <div>
                        <span className="font-medium">Valid Until:</span> {cert?.validUntil}
                      </div>
                      <div>
                        <span className="font-medium">Scope:</span> {cert?.scope}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'userControls' && (
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {securityData?.userControls?.title}
              </h2>
              <div className="space-y-6">
                {securityData?.userControls?.controls?.map((control, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border rounded-lg p-6"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {control?.category}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {control?.options?.map((option, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <span className="text-gray-700">{option}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Security Contact Section */}
        <div className="bg-red-50 rounded-lg p-6 mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Security Questions or Concerns?
          </h3>
          <p className="text-gray-600 mb-4">
            Report security vulnerabilities or get answers about our security practices.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Security Team:</h4>
              <p>
                <a 
                  href="mailto:security@leadgenproai.us" 
                  className="text-red-600 hover:text-red-700"
                >
                  security@leadgenproai.us
                </a>
              </p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Emergency Support:</h4>
              <p>
                <a 
                  href="tel:8449781051" 
                  className="text-red-600 hover:text-red-700"
                >
                  (844) 978-1051
                </a>
              </p>
            </div>
          </div>
        </div>
      </PageLayout>
    </>
  );
};

export default Security;